// ===========================================
// Invitations API Functions
// ===========================================
// API calls for parent and student invitation endpoints

import { apiClient, publicClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';
export type InvitationType = 'PARENT' | 'STUDENT';

export interface InvitationStatusResponse {
  hasEmail: boolean;
  status: InvitationStatus | 'NO_INVITATION' | 'ALREADY_REGISTERED';
  invitationId?: string;
  inviteeEmail?: string;
  inviteeFirstName?: string;
  inviteeLastName?: string;
  sentAt?: string;
  expiresAt?: string;
  acceptedAt?: string;
  canInvite: boolean;
  canResend: boolean;
}

// Student invitation status response
export interface StudentInvitationStatusResponse {
  hasEmail: boolean;
  hasAccount: boolean;
  status: 'NO_INVITATION' | 'PENDING' | 'EXPIRED' | 'ACCEPTED' | 'CANCELLED' | 'ALREADY_REGISTERED' | 'NO_EMAIL';
  invitationId?: string;
  inviteeEmail?: string;
  inviteeFirstName?: string;
  inviteeLastName?: string;
  sentAt?: string;
  expiresAt?: string;
  acceptedAt?: string;
  canInvite: boolean;
  canResend: boolean;
}

export interface InvitationSendResult {
  status: InvitationStatus;
  expiresAt: string;
  email: string;
}

export interface InvitationDetails {
  inviteeName: string;
  inviteeEmail: string;
  inviteeFirstName?: string;
  inviteeLastName?: string;
  familyName: string | null;
  studentName?: string;
  schoolName: string;
  schoolSlug?: string;
  expiresAt?: string;
  invitationType?: InvitationType;
}

export interface ValidateInvitationResult {
  valid: boolean;
  error?: 'EXPIRED' | 'ALREADY_USED' | 'CANCELLED' | 'NOT_FOUND';
  invitation?: InvitationDetails;
}

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// ===========================================
// AUTHENTICATED ENDPOINTS
// ===========================================

export const invitationsApi = {
  /**
   * Get invitation status for a parent's contact2
   * @param parentId - The parent ID to check
   */
  getStatus: (parentId: string) =>
    apiClient
      .get<ApiResponse<InvitationStatusResponse>>(`/invitations/parent/${parentId}/status`)
      .then((res) => res.data),

  /**
   * Send an invitation to contact2
   * @param parentId - The parent ID whose contact2 should be invited
   */
  sendInvitation: (parentId: string) =>
    apiClient
      .post<ApiResponse<InvitationSendResult>>(`/invitations/parent/${parentId}/send`)
      .then((res) => res.data),

  /**
   * Resend an invitation to contact2
   * @param parentId - The parent ID whose contact2 should receive a new invitation
   */
  resendInvitation: (parentId: string) =>
    apiClient
      .post<ApiResponse<InvitationSendResult>>(`/invitations/parent/${parentId}/resend`)
      .then((res) => res.data),

  /**
   * Cancel a pending invitation
   * @param parentId - The parent ID whose invitation should be cancelled
   */
  cancelInvitation: (parentId: string) =>
    apiClient.delete<ApiResponse<void>>(`/invitations/parent/${parentId}`),
};

// ===========================================
// STUDENT INVITATION ENDPOINTS (Admin Only)
// ===========================================

export const studentInvitationsApi = {
  /**
   * Get invitation status for a student
   * @param studentId - The student ID to check
   */
  getStatus: (studentId: string) =>
    apiClient
      .get<ApiResponse<StudentInvitationStatusResponse>>(`/invitations/student/${studentId}/status`)
      .then((res) => res.data),

  /**
   * Send an invitation to a student
   * @param studentId - The student ID who should be invited
   */
  sendInvitation: (studentId: string) =>
    apiClient
      .post<ApiResponse<InvitationSendResult>>(`/invitations/student/${studentId}/send`)
      .then((res) => res.data),

  /**
   * Resend an invitation to a student
   * @param studentId - The student ID who should receive a new invitation
   */
  resendInvitation: (studentId: string) =>
    apiClient
      .post<ApiResponse<InvitationSendResult>>(`/invitations/student/${studentId}/resend`)
      .then((res) => res.data),

  /**
   * Cancel a pending student invitation
   * @param studentId - The student ID whose invitation should be cancelled
   */
  cancelInvitation: (studentId: string) =>
    apiClient.delete<ApiResponse<void>>(`/invitations/student/${studentId}`),
};

// ===========================================
// PUBLIC ENDPOINTS (No Authentication)
// ===========================================

export const publicInvitationsApi = {
  /**
   * Validate an invitation token
   * @param token - The invitation token from the email link
   * @param type - Optional invitation type (student or parent)
   */
  validateToken: (token: string, type?: InvitationType) =>
    publicClient
      .get<ApiResponse<InvitationDetails>>(`/invitations/${token}/validate`, {
        params: type ? { type: type.toLowerCase() } : undefined,
      })
      .then((res) => res.data)
      .catch((error) => {
        // Return validation error structure
        if (error.response?.data) {
          return {
            valid: false,
            error: error.response.data.error || 'NOT_FOUND',
          } as ValidateInvitationResult;
        }
        throw error;
      }),

  /**
   * Accept an invitation and create account
   * @param token - The invitation token from the email link
   * @param password - The password for the new account
   * @param type - Optional invitation type (student or parent)
   * @param agreement - A PARENT invitation's two required ticks (#442 D1c); never sent for a student
   */
  acceptInvitation: (
    token: string,
    password: string,
    type?: InvitationType,
    agreement?: { termsAccepted: boolean; privacyAccepted: boolean }
  ) =>
    publicClient
      .post<ApiResponse<{ email: string; role: string }>>(`/invitations/${token}/accept`, { password, ...agreement }, {
        params: type ? { type: type.toLowerCase() } : undefined,
      })
      .then((res) => res.data),
};
